import {
  AfterContentInit,
  ChangeDetectorRef,
  ContentChild,
  DestroyRef,
  Directive,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subject, map, merge, scan } from 'rxjs';
import {
  FilterFn,
  FilterLogicalOperator,
  filterMultiple,
  sortMultiple,
} from '@fgb/portal-component-library/src/lib/shared/utilities';
import { isEqual, SortFn } from '@fgb/core';

export type SortOptions<Args extends Record<string, SortFn>> = {
  [key in keyof Args]: SortFn;
};

interface Page {
  [key: string]: any[];
}

type PageKeys = keyof Page;
export interface DataSourcePagination {
  pageNumber: number;
  pageSize: number | undefined;
  entities: Page;
  pages: PageKeys[];
  total: number;
}

/**
 * Reusable directive to handle data source events with sorting and filtering(not implemented yet)
 * Usage:
 *
 * @example
 *
 *   <div fgbDataSource [dataSource]="mixedLoyaltyList$ | async" [sortOptions]="sortOptions">
 *
 *     // Add ng-template to display the data, the let-items refers to the data source data is already sorted and filtered depending on the options passed in the
 *     // [sortOptions] input or [filters] input, the let-loaded refers to the loaded status of the data source
 *
 *     <ng-template let-items let-loaded="loaded">
 *
 *       // Check if the data source is loaded, if not display the loading template
 *       <ng-container *ngIf="loaded; else loading">
 *
 *           // Loop through the filtered or sorted items and display the items
 *           <ng-container *ngFor="let item of items">
 *               <div class="loyalty-item-container">
 *                 <fgbcl-mixed-loyalty-item [item]="item"></fgbcl-mixed-loyalty-item>
 *               </div>
 *           </ng-container>
 *
 *       </ng-container>
 *     </ng-template>
 *
 *     <ng-template #loading>loading...</ng-template>
 *  </div>
 *
 *  Sortings can be passed in the [sortOptions] input
 *  @example
 *  sortOptions = {
 *     status: sortBy((a: MixedLoyaltyItem, b: MixedLoyaltyItem) => a.status.status - b.status.status),
 *     ordinal: sortBy((a: MixedLoyaltyItem, b: MixedLoyaltyItem) => a.data.Ordinal - b.data.Ordinal)
 *   };
 *
 *  pass the input to sortOptions ex. <div fgbDataSource [dataSource]="mixedLoyaltyList$ | async" [sortOptions]="sortOptions">
 *
 *  Changing the sort direction example
 *
 *  @example
 *
 *  sortDirection: 'asc' | 'desc' = 'asc';
 *
 *  toggleSort(): void{
 *
 *    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
 *
 *    this.sortOptions = {
 *     status: sortBy((a: MixedLoyaltyItem, b: MixedLoyaltyItem) => a.status.status - b.status.status, this.sortDirection),
 *     ordinal: sortBy('name', this.sortDirection)
 *   };
 *  }
 *
 *  expect the data source to be sorted with the new toggled direction
 *
 * Filtering can be passed in the [filters] input
 * @example
 *
 * filters: FilterFn[] = [filterBy({ status: 'active' })];
 *
 * pass the input to filters ex. <div fgbDataSource [dataSource]="mixedLoyaltyList$ | async" [filters]="filters">
 *
 * Additionally you can add logical operators to the filters by adding { operator: 'or' } in between the filters
 * @example
 *
 * filters: FilterLogicalOperator[] = [filterBy({ name: 'test' }), { operator: 'or' }, filterBy({ status: 'active' })];
 */

export type LoadState = 'loading' | 'loaded' | 'empty';
@Directive({
  selector: '[fgbDataSource]',
  standalone: true,
})
export class DataSourceDirective implements OnInit, AfterContentInit {
  @Input() set dataSource(value: any[]) {
    this._dataSubject.next(value);
  }
  @Input() set sortOptions(options: SortOptions<any>) {
    this._sortSubject.next(options);
  }
  @Input() set page(page: number) {
    this._pageSubject.next(page);
  }
  @Input() set filters(filter: any[]) {
    this._filterSubject.next(filter);
  }
  @Input() pageSize: number;
  @Output() dataSourceChange: EventEmitter<any[] | null | undefined> = new EventEmitter<any[] | null | undefined>();
  @Output() paginationChange: EventEmitter<DataSourcePagination> = new EventEmitter<DataSourcePagination>();
  data: any[] | null | undefined;
  isLoaded: boolean = false;
  @ContentChild(TemplateRef, { static: true }) private template: TemplateRef<any>;

  private _dataSubject: BehaviorSubject<any[] | null> = new BehaviorSubject<any[] | null>(null);
  private _sortSubject: BehaviorSubject<SortOptions<any> | undefined> = new BehaviorSubject<SortOptions<any> | undefined>(
    undefined
  );
  private _pageSubject: Subject<number> = new Subject<number>();
  private _filterSubject: BehaviorSubject<any[] | null> = new BehaviorSubject<any[] | null>(null);
  private vc: ViewContainerRef = inject(ViewContainerRef);
  private _cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);
  templateInstace: any;

  ngOnInit(): void {
    // Merge events
    merge(
      this.onDataSourceChanged.pipe(map(this.dataHandler.bind(this))),
      this.onFilterChanged.pipe(map(this.filterHandler.bind(this))),
      this.onSortChanged.pipe(map(this.sortHandler.bind(this))),
      this.onPageChanged.pipe(map(this.pageHandler.bind(this)))
    )
      .pipe(
        // Handle the data source events, set the initial data source with empty data
        scan(this.eventHandler, this.buildContext()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((ctx: ReturnType<typeof this.buildContext>) => {
        if (!isEqual(this.data, ctx.data)) {
          // Notify the parent component that the data source has changed
          this.dataSourceChange.emit(ctx.data);
        }

        this.isLoaded = ctx.loaded;

        if (ctx.usePagination) {
          this.paginationChange.emit(ctx.pagination);
        }

        this.data = ctx.data;
        // Update the template context
        this.updateTemplateContext();
        this._cd.detectChanges();
      });
  }

  ngAfterContentInit(): void {
    // if ng-template is provided inside the directive, create an embedded view
    // otherwise if not provided use the (dataSourceChange) event to get the data
    // ex.
    // <div fgbDataSource [dataSource]="mixedLoyaltyList$ | async" [sortOptions]="sortOptions" (dataSourceChange)="data = $event">
    //    {{ data | json }}
    // </div>
    if (this.template) {
      this.templateInstace = this.vc.createEmbeddedView(this.template, {
        $implicit: this.data,
        loaded: false,
      }).context;
    }
  }

  private buildContext() {
    return {
      raw: null as any[] | null | undefined, // raw data before filtering and sorting, used for re-applying filters and sort when options change
      filters: [] as FilterFn[] | FilterLogicalOperator[] | null,
      sort: null as { [key: string]: SortFn } | null,
      status: 'loading' as LoadState,
      loaded: false,
      data: null as any[] | null | undefined,
      usePagination: this.pageSize !== undefined,
      pagination: {
        pageNumber: 1,
        pageSize: this.pageSize,
        entities: {} as Page,
        pages: [] as PageKeys[],
        total: 0,
      } as DataSourcePagination,
    };
  }

  /**
   * Handles all the data source events and updates the data source
   */
  private eventHandler(ctx: ReturnType<typeof this.buildContext>, eventFn: any) {
    return eventFn(ctx);
  }

  /**
   * Handle the data source event changes
   */
  private dataHandler(event: any) {
    return (ctx: ReturnType<typeof this.buildContext>) => {
      if (event === null) {
        // if event is null, return loading state, this handles loading state only if there is no
        // proper state management in place
        return { ...ctx, status: 'loading' };
      }

      // Set loaded to true if empty, if initial data is empty it will show empty placeholder instead
      // make sure initial data is null to avoid issues with loading states
      if (Array.isArray(event) && event.length === 0) {
        return { ...ctx, status: 'empty', data: [], loaded: true };
      }

      const raw = event;
      const data = event;
      const pagination = ctx.usePagination ? this.initializePagination({ ...ctx, raw, data }) : ctx.pagination;

      return this.deriveState({ ...ctx, raw, data, loaded: true, status: 'loaded', pagination });
    };
  }

  /**
   * Handle the page event changes
   */
  private pageHandler(pageNo: number) {
    return (ctx: ReturnType<typeof this.buildContext>) => {
      if (pageNo < 0) {
        // If pageNo is less than 0, return the state without updating the page
        return ctx;
      }

      let data = ctx.data;
      const pageData = ctx.pagination.entities[pageNo];

      if (pageData) {
        data = pageData;
      }

      return this.deriveState({ ...ctx, data, pagination: { ...ctx.pagination, pageNumber: pageNo } });
    };
  }

  /**
   * Handle the filter event changes
   */
  private filterHandler(filters: any[] | null) {
    return (ctx: ReturnType<typeof this.buildContext>) => this.deriveState({ ...ctx, filters });
  }

  /**
   * Helper function to apply sorting and filtering to the data
   */
  private applySortAndFilter(ctx: ReturnType<typeof this.buildContext>, data: any) {
    if (ctx.filters && ctx.filters.length > 0) {
      data = data?.filter(filterMultiple(...ctx.filters));
    }

    if (ctx.sort) {
      data = data?.sort(sortMultiple(...Object.values(ctx.sort)));
    }

    return data;
  }

  private deriveState(ctx: ReturnType<typeof this.buildContext>) {
    let data = ctx.data;
    const pagination = ctx.pagination;

    if (ctx.usePagination) {
      const pageNumber = pagination.pageNumber;
      const pageData = pagination.entities[pageNumber];

      if (pageData) {
        data = this.applySortAndFilter(ctx, pageData);
      }
    } else {
      data = this.applySortAndFilter(ctx, ctx.raw);
    }

    return { ...ctx, data };
  }

  private initializePagination(ctx: ReturnType<typeof this.buildContext>) {
    let pageEntities: Page = {};
    const totalSize = ctx.raw ? ctx.raw.length : 0;
    let pagination = ctx.pagination;
    const data = this.applySortAndFilter(ctx, ctx.raw);
    if (data) {
      const numberOfPages = Math.ceil(totalSize / (this.pageSize || totalSize));
      for (let i = 0; i < numberOfPages; i++) {
        pageEntities[i + 1] = data.slice(i * (this.pageSize || totalSize), (i + 1) * (this.pageSize || totalSize));
      }
    }
    pagination.entities = pageEntities;
    pagination.pages = Object.keys(pageEntities).map((key) => +key);
    pagination.total = totalSize;
    return pagination;
  }

  /**
   * Update the template context
   */
  private updateTemplateContext(): void {
    if (this.templateInstace) {
      this.templateInstace.$implicit = this.data;
      this.templateInstace.loaded = this.isLoaded;
    }
  }

  /**
   * Handle the sort event
   */
  private sortHandler(event: SortOptions<any> | undefined) {
    return (ctx: ReturnType<typeof this.buildContext>) => {
      if (event === undefined) {
        return ctx;
      }
      return this.deriveState({ ...ctx, sort: event });
    };
  }

  get onDataSourceChanged() {
    return this._dataSubject.asObservable();
  }

  get onSortChanged() {
    return this._sortSubject.asObservable();
  }

  get onPageChanged() {
    return this._pageSubject.asObservable();
  }

  get onFilterChanged() {
    return this._filterSubject.asObservable();
  }
}
